import { Drawer, ListItemButton, styled } from "@mui/material";
import type { Theme } from "@mui/material/styles";

/** Drawer width animation (ms); slower than MUI defaults so labels reveal smoothly with layout. */
export const NAV_DRAWER_TRANSITION_MS = 520;

/** Width of the icon column in each nav row. Kept narrow so the icon sits
 *  close to its label (less dead space between glyph and text). */
export const NAV_ICON_BOX_PX = 40;
/** Horizontal padding (px) to center the icon box in the collapsed rail (50px). */
const collapsedNavIconSidePadPx = (50 - NAV_ICON_BOX_PX) / 2;

// Inactive nav rows render WHITE — only the selected row gets the brand
// gradient tint. Hover shows a soft teal wash so the row stays interactive
// without coloring the entire sidebar.
const NAV_PARENT_GRADIENT_HOVER =
	"rgba(2, 213, 171, 0.06)";

type SidebarNavProps = {
	$width: number;
};

type NavMenuItemProps = {
	$isCollapsed: boolean;
	$navItemHeight: number;
	$isActive: boolean;
	$tileBackground: string;
};

const drawerWidthTransition = (theme: Theme) =>
	theme.transitions.create("width", {
		easing: theme.transitions.easing.easeInOut,
		duration: NAV_DRAWER_TRANSITION_MS,
	});

export const SidebarNav = styled(Drawer, {
	shouldForwardProp: (prop) => prop !== "$width",
})<SidebarNavProps>(({ theme, $width }) => ({
	width: $width,
	flexShrink: 0,
	zIndex: theme.zIndex.drawer,
	transition: drawerWidthTransition(theme),
	"& .MuiDrawer-paper": {
		width: $width,
		top: 0,
		height: "100%",
		boxSizing: "border-box",
		border: "none",
		backgroundColor: "#fafdfe",
		// No horizontal padding — nav rows are full width. Top padding 0 so the
		// 80px logo block lines up with the top app bar; bottom keeps spacing.
		padding: theme.spacing(0, 0, 2, 0),
		boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.15)",
		transition: drawerWidthTransition(theme),
		overflowX: "hidden",
	},
}));

export const NavMenuItem = styled(ListItemButton, {
	shouldForwardProp: (prop) => ![
		"$isCollapsed",
		"$navItemHeight",
		"$isActive",
		"$tileBackground",
	].includes(prop as string),
})<NavMenuItemProps>(({
	theme,
	$isCollapsed,
	$navItemHeight,
	$isActive,
	$tileBackground,
}) => ({
	borderRadius: 0,
	boxSizing: "border-box",
	position: "relative",
	transition: theme.transitions.create(["padding-left", "padding-right"], {
		duration: NAV_DRAWER_TRANSITION_MS,
		easing: theme.transitions.easing.easeInOut,
	}),
	// Selected indicator: pseudo-element only — expanded left inset stays constant so padding/border
	// never animate on select (that transition caused icon/label jitter). Collapse still animates padding.
	paddingLeft: $isCollapsed
		? collapsedNavIconSidePadPx
		: theme.spacing(1),
	paddingRight: $isCollapsed ? collapsedNavIconSidePadPx : theme.spacing(1.25),
	paddingTop: theme.spacing(0.5),
	paddingBottom: theme.spacing(0.5),
	minHeight: $navItemHeight,
	// Label spacing uses margin on the label wrapper so icon–label distance stays fixed while width reveals
	gap: 0,
	justifyContent: "flex-start",
	alignItems: "center",
	color: theme.palette.text.primary,
	background: $tileBackground,
	// Stay inside clipped drawer so the full row receives pointer events
	width: "100%",
	":hover": {
		background: NAV_PARENT_GRADIENT_HOVER,
	},
	"&.Mui-selected:hover": {
		background: NAV_PARENT_GRADIENT_HOVER,
	},
	"& .MuiListItemIcon-root": {
		minWidth: 0,
		// Default MUI minWidth (56) would push icons off-center when collapsed
		flexShrink: 0,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		color: "transparent",
		"& .MuiSvgIcon-root": {
			// Fixed size — do not scale up when the drawer expands
			fontSize: "24px",
			fill: "url(#navIconGradient)",
		},
	},
}));
